import Axios from "axios";

const coreApiBaseUrl =
  import.meta.env.VITE_CORE_API_URL || `${import.meta.env.VITE_BASE_URL}/api/v1`;

/**
 * Lagos rail timetable, from -> to.
 *
 * Everything here is schedule-derived. There is no live position feed for Lagos
 * rail, so the API answers with what is *scheduled* and the UI must say so —
 * a commuter who trusts a wrong "2 minutes away" misses their train.
 */

const TRANSIT = `${coreApiBaseUrl}/transport/train`;

export interface TransitStation {
  code: string;
  name: string;
  line: string | null;
  lineCode: string | null;
}

export interface TransitCall {
  seq: number;
  station: string;
  arrival: string | null;
  departure: string | null;
}

export interface TransitTrip {
  trainNo: string | null;
  direction: string;
  departs: string;
  arrives: string;
  durationMinutes: number | null;
  callsAt: TransitCall[];
}

export interface TransitSearchSupported {
  supported: true;
  line: { code: string; name: string; operator: string | null };
  direction: string;
  from: string;
  to: string;
  serviceDays: string | null;
  typicalDurationMinutes: number | null;
  total: number;
  trips: TransitTrip[];
}

export interface TransitSearchUnsupported {
  supported: false;
  reason: "unknown_station" | "no_through_service" | "no_service";
  message: string;
  candidates?: Array<{ station: string; line: string; lineCode: string }>;
}

export type TransitSearchResult =
  | TransitSearchSupported
  | TransitSearchUnsupported;

export interface TransitLineDirection {
  direction: string;
  label: string;
  tripCount: number;
  stations: string[];
}

export interface TransitLine {
  code: string;
  name: string;
  mode: string;
  operator: string | null;
  serviceDays: string | null;
  directions: TransitLineDirection[];
}

/**
 * The API wraps every response as { success, data, timestamp }; some payloads
 * then carry their own { total, data }. Peel exactly one layer.
 */
function unwrap<T>(payload: unknown): T {
  const body = payload as { data?: unknown } | undefined;
  return (body && typeof body === "object" && "data" in body
    ? body.data
    : payload) as T;
}

export async function listTransitStations(): Promise<TransitStation[]> {
  const res = await Axios.get(`${TRANSIT}/stations`);
  const inner = unwrap<{ data?: TransitStation[] }>(res.data);
  return Array.isArray(inner) ? inner : (inner?.data ?? []);
}

/**
 * The station catalogue is small and fixed, and several components need it at
 * once (both pickers plus the page itself). Share one in-flight request rather
 * than fetching it per consumer.
 */
let stationsPromise: Promise<TransitStation[]> | null = null;

export function getTransitStations(): Promise<TransitStation[]> {
  if (!stationsPromise) {
    stationsPromise = listTransitStations().catch((error) => {
      stationsPromise = null; // let a later mount retry
      throw error;
    });
  }
  return stationsPromise;
}

export async function listTransitLines(): Promise<TransitLine[]> {
  const res = await Axios.get(`${TRANSIT}/lines`);
  const inner = unwrap<{ data?: TransitLine[] }>(res.data);
  return Array.isArray(inner) ? inner : (inner?.data ?? []);
}

export async function searchTransit(params: {
  from: string;
  to: string;
  after?: string;
  limit?: number;
}): Promise<TransitSearchResult> {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  if (params.after) query.set("after", params.after);
  if (params.limit) query.set("limit", String(params.limit));

  const res = await Axios.get(`${TRANSIT}/search?${query.toString()}`);
  return unwrap<TransitSearchResult>(res.data);
}

// ----- reminders ---------------------------------------------------------

export type TransitAlertEvent = "departure" | "approach" | "waypoint";

export interface TransitAlert {
  id: string;
  label: string | null;
  origin: string | null;
  destination: string | null;
  waypoint: string | null;
  daysOfWeek: number[];
  windowStart: string;
  windowEnd: string;
  events: TransitAlertEvent[];
  leadMinutes: number;
  isActive: boolean;
  nextFire: {
    eventType: string;
    station: string;
    trainNo: string | null;
    scheduledAt: string;
    fireAt: string;
  } | null;
}

export interface CreateTransitAlertInput {
  /** Identity and delivery address in one: whose reminder, and where it goes. */
  email: string;
  /** Reserved for SMS — accepted and stored, but nothing delivers to it yet. */
  phone?: string;
  origin: string;
  destination: string;
  waypoint?: string;
  daysOfWeek: number[];
  windowStart: string;
  windowEnd: string;
  events: TransitAlertEvent[];
  leadMinutes: number;
}

export async function listTransitAlerts(
  email: string,
): Promise<TransitAlert[]> {
  const res = await Axios.get(
    `${TRANSIT}/alerts?email=${encodeURIComponent(email)}`,
  );
  const inner = unwrap<{ data?: TransitAlert[] }>(res.data);
  return Array.isArray(inner) ? inner : (inner?.data ?? []);
}

export async function createTransitAlert(
  input: CreateTransitAlertInput,
): Promise<TransitAlert> {
  const res = await Axios.post(`${TRANSIT}/alerts`, input);
  return unwrap<TransitAlert>(res.data);
}

export async function deleteTransitAlert(
  id: string,
  email: string,
): Promise<void> {
  await Axios.delete(
    `${TRANSIT}/alerts/${id}?email=${encodeURIComponent(email)}`,
  );
}

/** "06:00:00" -> "06:00". Seconds are real in the timetable but noise on screen. */
export function hhmm(time: string | null): string {
  return time ? time.slice(0, 5) : "—";
}

export function formatDuration(minutes: number | null): string {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}
